import React from "react";
import { getAttribution } from "../../utils/analytics";

// Pieces shared by the signup and sign-in screens (pages/LoginPage.jsx), from
// the "Your first hire is on us" design. Styles: css/signupFlow.css.

// The free plan every signup is given: plan_trial in
// backend/Scripts/seed_self_serve_plans.py. Change both together.
export const FREE_INTERVIEWS = 10;
export const FREE_CV_SCREENINGS = 500;

export const TERMS_URL = "https://geniehire.ai/term-condition";
export const PRIVACY_URL = "https://geniehire.ai/privacy-policy";
export const SALES_EMAIL = "sales@geniehire.ai";
export const PLATFORM_ADDRESS = "platform.geniehire.ai";

// The subject of the phone signup's link email, which the "Link sent" screen
// tells people to look for. Same as SIGNUP_LINK_SUBJECT in backend
// models/automails.py; change both together.
export const LINK_EMAIL_SUBJECT = "Set up your GenieHire account";

// Phones and tablets sign up by emailing themselves a link to open on a
// laptop: the app is used on a laptop or desktop. Decided by the device, not
// the window width, so a narrow laptop window still gets the code.
export const isHandheld = () => {
  if (typeof navigator === "undefined") return false;
  if (navigator.userAgentData?.mobile) return true;
  const ua = navigator.userAgent || "";
  if (/Android|iPhone|iPad|iPod|Mobile|Tablet|Silk|Kindle|BlackBerry|Opera Mini|IEMobile/i.test(ua)) return true;
  // iPadOS reports itself as a Mac; a Mac has no touch screen
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
};

// The handwritten line above the headline repeats the ad they clicked. Ads
// send their full name as utm_content ("GH | IN | Self-serve signup | PasteJD
// | IMG-A | v1"); the theme word in it picks the line. Anything else gets the
// default.
const AD_NOTES = [
  ["makeawish", "Make a wish. Meet your shortlist."],
  ["owntime", "They interview tonight. You review at 9."],
  ["nocall", "No sales call. No quote. Just your shortlist."],
  ["pastejd", "214 CVs in. A ranked shortlist out."],
  ["smallteam", "One of you. 312 applicants."],
  ["firstround", "The first round's on us."],
  ["everyoneheard", "Everyone gets heard. The best get hired."],
];
const DEFAULT_AD_NOTE = "Interview everyone. Hire the best.";

export const adNote = () => {
  let content = "";
  try {
    content = new URLSearchParams(window.location.search).get("utm_content")
      || getAttribution()?.utm_content
      || "";
  } catch {
    /* no URL or storage: the default line */
  }
  const key = content.toLowerCase().replace(/[^a-z]/g, "");
  return (AD_NOTES.find(([word]) => key.includes(word)) || [])[1] || DEFAULT_AD_NOTE;
};

// An illustration on step 1, labelled "Example" on the page
export const EXAMPLE_ROWS = [
  { name: "Kavya Shetty", when: "Wed 07:40", score: 91 },
  { name: "Meera Iyer", when: "Tue 22:14", score: 86 },
  { name: "Rohit Nair", when: "Tue 23:05", score: 84 },
];

// "2026-11-05T11:24:00Z" -> "5 Nov, 4:54 pm" in the visitor's time zone
export const formatUntil = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch {
    return "";
  }
};

// "today", "yesterday", "3 days ago"
export const daysAgo = (iso) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (!(days > 0)) return "today";
  return days === 1 ? "yesterday" : `${days} days ago`;
};

export const Arrow = ({ color = "#FFFFFF" }) => (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

export const Logo = () => <img src="/genie-demo-logo-black.png" alt="GenieHire.ai" className="gh-logo" />;

export const Check = ({ color = "#2F6B3A", size = 16 }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
);

const LaptopIcon = () => (
  <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#141414"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none", marginTop: 1 }}>
    <rect x="3" y="4" width="18" height="12" rx="1.5" /><path d="M1.5 19.5h21" />
  </svg>
);

export const LaptopPicture = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: "6px 0 2px" }} aria-hidden="true">
    <svg width="150" height="92" viewBox="0 0 150 92" fill="none">
      <rect x="22" y="6" width="106" height="68" rx="6" fill="#F5F2EC" stroke="#141414" strokeWidth="2.4" />
      <rect x="34" y="18" width="52" height="7" rx="3.5" fill="#141414" />
      <rect x="34" y="31" width="82" height="5" rx="2.5" fill="#D6CFC3" />
      <rect x="34" y="41" width="70" height="5" rx="2.5" fill="#D6CFC3" />
      <rect x="34" y="53" width="38" height="11" rx="5.5" fill="#C21AB8" />
      <path d="M6 80h138l-8 8H14z" fill="#141414" />
    </svg>
  </div>
);

export const Agreement = () => (
  <>
    By continuing you agree to the{" "}
    <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a>,{" "}
    <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>{" "}
    and updates on WhatsApp.
  </>
);

/** One message screen of the flow (link sent, link expired, ask to join...):
 *  the phone boards' layout, centred in a narrow column on a laptop. */
export const StatusScreen = ({ eyebrow, title, accent, children }) => (
  <div className="gh-page">
    <header className="gh-header"><Logo /></header>
    <main className="gh-status">
      <div className="gh-status-head">
        <span className="gh-eyebrow">{eyebrow}</span>
        <h1 className="gh-status-title">
          {title}{accent && <> <span className="gh-accent">{accent}</span></>}
        </h1>
      </div>
      {children}
    </main>
  </div>
);

export const NoteBox = ({ children }) => (
  <div className="gh-notebox"><LaptopIcon /><span>{children}</span></div>
);

/** Step 1 on a phone or tablet: the phone board. The form asks for the work
 *  email only; what happens next is a link to open on a laptop. */
export const PhoneLanding = ({
  note, email, setEmail, emailRef, emailFieldProps, onSubmit, submitting, messages, goToLogin,
}) => {
  const claim = () => {
    document.getElementById("signup")?.scrollIntoView({ behavior: "smooth", block: "center" });
    emailRef.current?.focus({ preventScroll: true });
  };
  return (
    <div className="gh-page gh-m">
      <header className="gh-m-header">
        <Logo />
        <button type="button" className="gh-link gh-m-signin" onClick={goToLogin}>Sign in</button>
      </header>

      <section className="gh-m-hero">
        <p className="gh-hand" style={{ fontSize: 20, transform: "rotate(-2deg)" }}>{note}</p>
        <h1 className="gh-m-h1"><span>Your first hire</span><span className="gh-accent">is on us.</span></h1>
        <p className="gh-m-lead">
          We run the first-round video interviews for your next role. You watch the answers and pick who you hire.
        </p>

        <form id="signup" className="gh-ticket gh-m-ticket" onSubmit={onSubmit}>
          <div className="gh-ticket-top">
            <span className="gh-ticket-num">{FREE_INTERVIEWS}</span>
            <span className="gh-ticket-title">
              <strong>AI video interviews, free</strong>
              <span>Plus {FREE_CV_SCREENINGS} CV screenings if you need them</span>
            </span>
          </div>
          <div className="gh-perforation" aria-hidden="true"><span /><span /></div>
          <div className="gh-ticket-body">
            <label htmlFor="gh-email" className="gh-label">Claim them with your work email</label>
            <input
              id="gh-email"
              ref={emailRef}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@yourcompany.com"
              className="gh-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              {...emailFieldProps}
              required
            />
            {messages}
            <button type="submit" className="gh-button" disabled={submitting}>
              {submitting ? "Sending your link..." : `Claim my ${FREE_INTERVIEWS} interviews`}
            </button>
            <p className="gh-fineprint">No card required. No sales call. <Agreement /></p>
          </div>
        </form>
      </section>

      <section className="gh-band gh-m-how">
        <div className="gh-how-head">
          <span className="gh-eyebrow">How the AI round works</span>
          <h2 className="gh-m-h2">AI interviews. <span className="gh-accent">You decide.</span></h2>
        </div>
        <ol className="gh-m-steps">
          <li><span>1</span><span><strong>Send your shortlist one link</strong><span>No call slots to fix, no "call me later".</span></span></li>
          <li><span>2</span><span><strong>They interview in their own time</strong><span>On a laptop, evenings included. Every candidate gets the same questions.</span></span></li>
          <li><span>3</span><span><strong>You get scores, recordings and transcripts</strong><span>Play any answer. Decide who you meet in person.</span></span></li>
        </ol>
        <div className="gh-m-example">
          <div className="gh-example-head">
            <span className="gh-mono">Wed 9:02 am · Inside sales</span>
            <span className="gh-example-tag">Example</span>
          </div>
          {EXAMPLE_ROWS.map((row) => (
            <div key={row.name} className="gh-m-example-row">
              <span><b>{row.name}</b><span className="gh-mono">Completed {row.when}</span></span>
              <span className="gh-example-score">{row.score}</span>
            </div>
          ))}
        </div>
        <p className="gh-hand" style={{ fontSize: 19, margin: "0 0 0 auto", alignSelf: "flex-end", transform: "rotate(-2deg)" }}>
          Interviewed while you were asleep.
        </p>
      </section>

      <section className="gh-m-faq">
        <div><h3>Already shortlisted in your own tools?</h3><p>Upload just those candidates' CVs and send them the interview link. The free CV screenings cover it.</p></div>
        <div><h3>Do candidates need to install anything?</h3><p>No. They open the link on a laptop and start.</p></div>
        <div><h3>Why a work email?</h3><p>The free interviews belong to your company. Teammates you invite share them. When they run out, you choose a plan in the app; nothing is charged on its own.</p></div>
      </section>

      <footer className="gh-m-footer">
        <span>© {new Date().getFullYear()} NSquare Innovators Inc.</span>
        <span>
          <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy</a>
          <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a>
          <a href="/grievance-form">Help</a>
        </span>
      </footer>

      <div className="gh-m-bar">
        <span><strong>{FREE_INTERVIEWS} AI interviews</strong> free</span>
        <button type="button" className="gh-button" onClick={claim}>Claim them</button>
      </div>
    </div>
  );
};
